import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiPut, apiUpload } from '../api/http';
import './LoanAdmin.css';

const DEFAULT_SALARIED_SLABS = [
  { min: 0, max: 50000, percent: 50 },
  { min: 50000, max: 75000, percent: 55 },
  { min: 75000, max: 100000, percent: 60 },
  { min: 100000, max: 150000, percent: 65 },
  { min: 150000, max: 250000, percent: 70 },
  { min: 250000, max: 300000, percent: 75 },
  { min: 300000, max: 400000, percent: 80 },
  { min: 400000, max: '', percent: 85 }
];

const DEFAULT_SELF_EMPLOYED_SLABS = [
  { min: 0, max: 50000, percent: 50 },
  { min: 50000, max: 100000, percent: 85 },
  { min: 100000, max: 150000, percent: 90 },
  { min: 150000, max: '', percent: 95 }
];

const DEFAULT_TURNOVER_FACTORS = {
  Service: 0.14,
  Manufacturing: 0.08,
  Trading: 0.08,
  Distributor: 0.03,
  Other: 0.08
};

const DEFAULT_ABB_STEPS = [
  { min: '', max: '', percent: '' }
];

const DEFAULT_LOW_LTV_STEPS = [
  { min: 0, max: 7500000, percent: 80 },
  { min: 7500000, max: '', percent: 75 }
];

const IMPORT_PRODUCT_TYPES = ['FOIR', 'DOUBLE_WHAMMY', 'ABB', 'TURNOVER', 'LOW_LTV', 'LTV'];

function ImportSummaryTable({ summary, title }) {
  if (!summary) return null;

  return (
    <div style={{ marginTop: '1em' }}>
      {title ? <h4 style={{ margin: '0 0 0.6em' }}>{title}</h4> : null}
      {summary.banks ? (
        <div className="loan-admin-summary-line">
          Banks in workbook: <strong>{summary.banks.applied ?? 0}</strong>
          {' '}| New: <strong>{summary.banks.created ?? 0}</strong>
          {' '}| Renamed: <strong>{summary.banks.updated ?? 0}</strong>
          {' '}| Existing: <strong>{summary.banks.unchanged ?? 0}</strong>
        </div>
      ) : null}
      <div className="loan-admin-summary-line">
        Total loan products: <strong>{summary.totalProducts ?? summary.applied ?? 0}</strong>
        {' '}| Created: <strong>{summary.created ?? 0}</strong>
        {' '}| Updated: <strong>{summary.updated ?? 0}</strong>
        {' '}| Deactivated: <strong>{summary.deactivated ?? 0}</strong>
        {summary.orphanMappingsRemoved ? (
          <> {' '}| Cleaned stale mappings: <strong>{summary.orphanMappingsRemoved}</strong></>
        ) : null}
      </div>
      <table>
        <thead>
          <tr>
            <th>Product Type</th>
            <th>Created</th>
            <th>Updated</th>
            <th>Deactivated</th>
            <th>Total Applied</th>
          </tr>
        </thead>
        <tbody>
          {IMPORT_PRODUCT_TYPES.map((type) => {
            const row = summary.byType?.[type] || {};
            return (
              <tr key={type}>
                <td>{type}</td>
                <td>{row.created ?? 0}</td>
                <td>{row.updated ?? 0}</td>
                <td>{row.deactivated ?? 0}</td>
                <td>{row.applied ?? row.products ?? 0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function cloneSlabs(slabs) {
  return (Array.isArray(slabs) ? slabs : []).map((x) => ({
    min: x.min ?? '',
    max: x.max ?? '',
    percent: x.percent ?? ''
  }));
}

function numberOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizePropertyTypes(text) {
  return String(text || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

function createEmptyForm() {
  return {
    id: null,
    bankId: '',
    product: 'FOIR',
    maxFOIR: '',
    maxLTV: '',
    maxCombined: '',
    maxLoanCap: '',
    enforceMaxLoanCap: false,
    interestRateOverridePct: '',
    maxTenureMonths: '',
    ambFactor: '',
    abbEmiFactor: '0.5',
    industry: '',
    turnoverFactor: '',
    turnoverSectorFactors: { ...DEFAULT_TURNOVER_FACTORS },
    foirSlabsSalaried: cloneSlabs(DEFAULT_SALARIED_SLABS),
    foirSlabsSelfEmployed: cloneSlabs(DEFAULT_SELF_EMPLOYED_SLABS),
    abbSteps: cloneSlabs(DEFAULT_ABB_STEPS),
    lowLtvSteps: cloneSlabs(DEFAULT_LOW_LTV_STEPS),
    propertyTypesText: ''
  };
}

function SlabEditor({ title, slabs, onChange }) {
  function updateCell(index, key, value) {
    const next = slabs.map((row, i) => (i === index ? { ...row, [key]: value } : row));
    onChange(next);
  }

  function addRow() {
    onChange([...slabs, { min: '', max: '', percent: '' }]);
  }

  function removeRow(index) {
    onChange(slabs.filter((_, i) => i !== index));
  }

  return (
    <div className="card" style={{ marginTop: '1em' }}>
      <h4 style={{ marginTop: 0 }}>{title}</h4>
      <table>
        <thead>
          <tr>
            <th>Min Monthly Income</th>
            <th>Max Monthly Income</th>
            <th>FOIR %</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {slabs.map((row, index) => (
            <tr key={`${title}-${index}`}>
              <td>
                <input
                  value={row.min}
                  onChange={(e) => updateCell(index, 'min', e.target.value)}
                />
              </td>
              <td>
                <input
                  value={row.max}
                  onChange={(e) => updateCell(index, 'max', e.target.value)}
                  placeholder="blank = no upper limit"
                />
              </td>
              <td>
                <input
                  value={row.percent}
                  onChange={(e) => updateCell(index, 'percent', e.target.value)}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="btn danger small"
                  onClick={() => removeRow(index)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: '0.75em' }}>
        <button type="button" className="btn secondary small" onClick={addRow}>
          Add Slab
        </button>
      </div>
    </div>
  );
}

export default function LoanAdmin() {
  const [banks, setBanks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newBank, setNewBank] = useState('');
  const [editBank, setEditBank] = useState(null);
  const [productForm, setProductForm] = useState(createEmptyForm());
  const productEditorRef = useRef(null);
  const importFileInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importValidation, setImportValidation] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importNotice, setImportNotice] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const bankData = await apiGet('/api/superadmin/banks');
      const productData = await apiGet('/api/superadmin/loan-products');
      setBanks(bankData);
      setProducts(productData);
    } catch (err) {
      alert(err?.message || 'Failed to load banks/products');
    } finally {
      setLoading(false);
    }
  }

  async function addBank() {
    if (!newBank.trim()) return;
    await apiPost('/api/superadmin/banks', { name: newBank.trim() });
    setNewBank('');
    await loadAll();
  }

  async function updateBank(id, name) {
    const nextName = String(name || '').trim();
    if (!nextName) return;
    await apiPut(`/api/superadmin/banks/${id}`, { name: nextName });
    setEditBank(null);
    await loadAll();
  }

  async function deleteBank(id) {
    if (!window.confirm('Delete this bank?')) return;
    await apiDelete(`/api/superadmin/banks/${id}`);
    await loadAll();
  }

  function startEditProduct(p) {
    setProductForm({
      id: p.id,
      bankId: p.bank_id || p.bankId || '',
      product: p.product || 'FOIR',
      maxFOIR: p.max_foir ?? p.maxFOIR ?? '',
      maxLTV: p.max_ltv ?? p.maxLTV ?? '',
      maxCombined: p.max_combined ?? p.maxCombined ?? '',
      maxLoanCap: p.max_loan_cap ?? p.maxLoanCap ?? '',
      enforceMaxLoanCap: (p.enforce_max_loan_cap ?? p.enforceMaxLoanCap) != null
        ? !!(p.enforce_max_loan_cap ?? p.enforceMaxLoanCap)
        : Number(p.max_loan_cap ?? p.maxLoanCap ?? 0) > 0,
      interestRateOverridePct: p.interest_rate_override_pct ?? p.interestRateOverridePct ?? '',
      maxTenureMonths: p.max_tenure_months ?? p.maxTenureMonths ?? '',
      ambFactor: p.amb_factor ?? p.ambFactor ?? '',
      abbEmiFactor: p.abb_emi_factor ?? p.abbEmiFactor ?? p.amb_factor ?? p.ambFactor ?? '0.5',
      industry: p.industry || '',
      turnoverFactor: p.turnover_factor ?? p.turnoverFactor ?? '',
      turnoverSectorFactors: {
        ...DEFAULT_TURNOVER_FACTORS,
        ...(p.turnoverSectorFactors || {})
      },
      foirSlabsSalaried: cloneSlabs(
        p.foirSlabsSalaried && p.foirSlabsSalaried.length > 0
          ? p.foirSlabsSalaried
          : DEFAULT_SALARIED_SLABS
      ),
      foirSlabsSelfEmployed: cloneSlabs(
        p.foirSlabsSelfEmployed && p.foirSlabsSelfEmployed.length > 0
          ? p.foirSlabsSelfEmployed
          : DEFAULT_SELF_EMPLOYED_SLABS
      ),
      abbSteps: cloneSlabs(
        p.abbSteps && p.abbSteps.length > 0
          ? p.abbSteps
          : DEFAULT_ABB_STEPS
      ),
      lowLtvSteps: cloneSlabs(
        p.lowLtvSteps && p.lowLtvSteps.length > 0
          ? p.lowLtvSteps
          : DEFAULT_LOW_LTV_STEPS
      ),
      propertyTypesText: Array.isArray(p.propertyTypes) ? p.propertyTypes.join(', ') : ''
    });

    window.requestAnimationFrame(() => {
      productEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function resetProductForm() {
    setProductForm(createEmptyForm());
  }

  function setProductField(name, value) {
    setProductForm((prev) => ({ ...prev, [name]: value }));
  }

  function setTurnoverFactorField(key, value) {
    setProductForm((prev) => ({
      ...prev,
      turnoverSectorFactors: {
        ...prev.turnoverSectorFactors,
        [key]: value
      }
    }));
  }

  async function saveProduct() {
    const data = {
      bankId: Number(productForm.bankId),
      product: productForm.product,
      maxFOIR: numberOrNull(productForm.maxFOIR),
      maxLTV: numberOrNull(productForm.maxLTV),
      maxCombined: numberOrNull(productForm.maxCombined),
      maxLoanCap: numberOrNull(productForm.maxLoanCap),
      enforceMaxLoanCap: !!productForm.enforceMaxLoanCap,
      interestRateOverridePct: numberOrNull(productForm.interestRateOverridePct),
      maxTenureMonths: numberOrNull(productForm.maxTenureMonths),
      ambFactor: numberOrNull(productForm.ambFactor),
      abbEmiFactor: numberOrNull(productForm.abbEmiFactor),
      industry: productForm.industry || null,
      turnoverFactor: numberOrNull(productForm.turnoverFactor),
      turnoverSectorFactors: {
        Service: Number(productForm.turnoverSectorFactors.Service || 0),
        Manufacturing: Number(productForm.turnoverSectorFactors.Manufacturing || 0),
        Trading: Number(productForm.turnoverSectorFactors.Trading || 0),
        Distributor: Number(productForm.turnoverSectorFactors.Distributor || 0),
        Other: Number(productForm.turnoverSectorFactors.Other || 0)
      },
      foirSlabsSalaried: productForm.foirSlabsSalaried.map((x) => ({
        min: numberOrNull(x.min) ?? 0,
        max: x.max === '' || x.max === null || x.max === undefined ? null : Number(x.max),
        percent: numberOrNull(x.percent) ?? 0
      })),
      foirSlabsSelfEmployed: productForm.foirSlabsSelfEmployed.map((x) => ({
        min: numberOrNull(x.min) ?? 0,
        max: x.max === '' || x.max === null || x.max === undefined ? null : Number(x.max),
        percent: numberOrNull(x.percent) ?? 0
      })),
      abbSteps: productForm.abbSteps.map((x) => ({
        min: numberOrNull(x.min) ?? 0,
        max: x.max === '' || x.max === null || x.max === undefined ? null : Number(x.max),
        percent: numberOrNull(x.percent) ?? 0
      })),
      lowLtvSteps: productForm.lowLtvSteps.map((x) => ({
        min: numberOrNull(x.min) ?? 0,
        max: x.max === '' || x.max === null || x.max === undefined ? null : Number(x.max),
        percent: numberOrNull(x.percent) ?? 0
      })),
      propertyTypes: normalizePropertyTypes(productForm.propertyTypesText)
    };

    if (!data.bankId) {
      alert('Please select a bank.');
      return;
    }

    if (productForm.id) {
      await apiPut(`/api/superadmin/loan-products/${productForm.id}`, data);
    } else {
      await apiPost('/api/superadmin/loan-products', data);
    }

    resetProductForm();
    await loadAll();
  }

  async function deleteProduct(id) {
    if (!window.confirm('Delete this loan product?')) return;
    await apiDelete(`/api/superadmin/loan-products/${id}`);
    await loadAll();
  }

  function downloadLoanProductsMasterExcel() {
    // Use a normal browser download instead of fetch + blob. This is more reliable
    // under production security headers and cPanel/proxy deployments.
    const stamp = new Date().toISOString().slice(0, 10);
    const cacheBust = encodeURIComponent(String(Date.now()));
    const link = document.createElement('a');
    link.href = `/api/superadmin/loan-products/master-excel?download=${cacheBust}`;
    link.download = `LoanProgress_LoanProducts_Master_${stamp}.xlsx`;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    window.setTimeout(() => link.remove(), 0);
    setImportNotice('Download requested. Check your browser downloads.');
  }

  function handleImportFileChange(e) {
    const file = e.target.files?.[0] || null;
    setImportFile(file);
    setImportValidation(null);
    setImportResult(null);
    setImportNotice('');
  }

  async function validateLoanProductsExcel() {
    if (!importFile) {
      alert('Please choose the loan products Excel file first.');
      return;
    }

    setImportBusy(true);
    setImportResult(null);
    setImportNotice('');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const result = await apiUpload('/api/superadmin/loan-products/import/validate', formData);
      setImportValidation(result);
      setImportNotice(result?.valid ? 'Validation passed. Apply Import is now enabled.' : 'Validation found issues. Fix the listed items and validate again.');
    } catch (err) {
      alert(err?.message || 'Excel validation failed.');
    } finally {
      setImportBusy(false);
    }
  }

  async function applyLoanProductsExcel() {
    if (!importFile) {
      alert('Please choose the loan products Excel file first.');
      return;
    }

    if (!importValidation?.valid) {
      alert('Please validate the Excel file successfully before applying it.');
      return;
    }

    if (!window.confirm('Apply this Excel import now? Banks listed on the Banks worksheet will be created/renamed first. Products in the Products sheet will be overwritten, and existing active loan products missing from Products will be deactivated.')) {
      return;
    }

    setImportBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const result = await apiUpload('/api/superadmin/loan-products/import/apply', formData);
      setImportResult(result);
      setImportNotice('Import applied successfully. Download a fresh master Excel to continue with the latest bank dropdowns.');
      setImportValidation(null);
      setImportFile(null);
      if (importFileInputRef.current) importFileInputRef.current.value = '';
      await loadAll();
    } catch (err) {
      alert(err?.message || 'Excel import failed.');
    } finally {
      setImportBusy(false);
    }
  }

  async function clearAllLoanProducts() {
    const confirmation = window.prompt('This will delete all loan products and related rules, but it will not delete banks. Type CLEAR LOAN PRODUCTS to continue.');
    if (confirmation !== 'CLEAR LOAN PRODUCTS') return;

    setImportBusy(true);
    try {
      const result = await apiPost('/api/superadmin/loan-products/clear-all', { confirmation });
      setImportResult({
        valid: true,
        summary: {
          totalProducts: result.totalDeleted || 0,
          created: 0,
          updated: 0,
          applied: 0,
          byType: IMPORT_PRODUCT_TYPES.reduce((acc, type) => {
            acc[type] = { created: 0, updated: 0, applied: result.byType?.[type] || 0 };
            return acc;
          }, {})
        }
      });
      setImportNotice('All loan products were cleared. Banks were not deleted.');
      await loadAll();
    } catch (err) {
      alert(err?.message || 'Failed to clear loan products.');
    } finally {
      setImportBusy(false);
    }
  }

  const displayRows = useMemo(() => {
    return [...products].sort((a, b) => {
      const bankCompare = String(a.bankName || '').localeCompare(String(b.bankName || ''));
      if (bankCompare !== 0) return bankCompare;
      return String(a.product || '').localeCompare(String(b.product || ''));
    });
  }, [products]);

  if (loading) {
    return <div className="card">Loading...</div>;
  }

  return (
    <div className="card loan-admin-page">
      <h2>Superadmin: Bank & Loan Product Management</h2>

      <div className="card" style={{ marginTop: '1em' }}>
        <h3>Banks</h3>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {banks.map((b) => (
              <tr key={b.id}>
                <td>{b.id}</td>
                <td>
                  {editBank === b.id ? (
                    <input
                      defaultValue={b.name}
                      autoFocus
                      onBlur={(e) => updateBank(b.id, e.target.value)}
                    />
                  ) : (
                    b.name
                  )}
                </td>
                <td>
                  <button className="btn secondary small" onClick={() => setEditBank(b.id)}>
                    Edit
                  </button>
                  <button className="btn danger small" onClick={() => deleteBank(b.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: '1em' }}>
          <label>
            New Bank Name
            <input value={newBank} onChange={(e) => setNewBank(e.target.value)} />
          </label>
          <button className="btn" onClick={addBank}>
            Add Bank
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1em' }}>
        <h3>Loan Products</h3>

        <div className="card loan-admin-import-card" style={{ marginBottom: '1em' }}>
          <h4>Loan Products Master Excel</h4>
          <div className="loan-admin-import-help">
            Download the current master Excel, update the Banks, Products, and rule/slab worksheets, validate it, then apply it. The Products sheet is treated as the master list: rows removed from Products will deactivate those loan products after import.
          </div>

          <div className="loan-admin-import-actions">
            <button className="btn secondary" type="button" onClick={downloadLoanProductsMasterExcel} disabled={importBusy}>
              Download Master Excel
            </button>

            <input
              ref={importFileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleImportFileChange}
              disabled={importBusy}
            />

            <button className="btn secondary" type="button" onClick={validateLoanProductsExcel} disabled={!importFile || importBusy}>
              Validate Excel
            </button>

            <button className="btn" type="button" onClick={applyLoanProductsExcel} disabled={!importValidation?.valid || importBusy}>
              Apply Import
            </button>

            <button className="btn danger" type="button" onClick={clearAllLoanProducts} disabled={importBusy}>
              Clear All Loan Products
            </button>
          </div>

          {importBusy ? <div className="loan-admin-import-status">Working...</div> : null}
          {!importBusy && importNotice ? <div className="loan-admin-import-status">{importNotice}</div> : null}

          {importValidation ? (
            <div style={{ marginTop: '1em' }}>
              <div className={importValidation.valid ? 'loan-admin-validation-ok' : 'loan-admin-validation-error'}>
                Validation: <strong>{importValidation.valid ? 'Passed' : 'Needs fixes'}</strong>
              </div>
              <ImportSummaryTable summary={importValidation.summary} title="Validation Summary" />
              {importValidation.errors?.length ? (
                <div style={{ marginTop: '1em' }}>
                  <h4 style={{ margin: '0 0 0.6em' }}>Fix these before applying</h4>
                  <ul>
                    {importValidation.errors.slice(0, 25).map((error, index) => (
                      <li key={`${error}-${index}`}>{error}</li>
                    ))}
                  </ul>
                  {importValidation.errors.length > 25 ? (
                    <div>Showing first 25 errors out of {importValidation.errors.length}.</div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {importResult ? (
            <ImportSummaryTable summary={importResult.summary} title="Applied Summary" />
          ) : null}
        </div>

        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Bank</th>
              <th>Product</th>
              <th>FOIR</th>
              <th>LTV</th>
              <th>Combined</th>
              <th>Cap</th>
              <th>Cap Enforced</th>
              <th>Interest%</th>
              <th>Tenure</th>
              <th>AMB Factor</th>
              <th>ABB EMI Factor</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((p) => (
              <tr
                key={p.id}
                onClick={() => startEditProduct(p)}
                style={{ cursor: 'pointer', background: productForm.id === p.id ? '#f6f9ff' : undefined }}
                title="Click to edit this loan product"
              >
                <td>{p.id}</td>
                <td>{p.bankName}</td>
                <td>{p.product}</td>
                <td>{p.max_foir ?? p.maxFOIR ?? ''}</td>
                <td>{p.max_ltv ?? p.maxLTV ?? ''}</td>
                <td>{p.max_combined ?? p.maxCombined ?? ''}</td>
                <td>{p.max_loan_cap ?? p.maxLoanCap ?? ''}</td>
                <td>{((p.enforce_max_loan_cap ?? p.enforceMaxLoanCap) != null ? (p.enforce_max_loan_cap ?? p.enforceMaxLoanCap) : Number(p.max_loan_cap ?? p.maxLoanCap ?? 0) > 0) ? 'Yes' : 'No'}</td>
                <td>{p.interest_rate_override_pct ?? p.interestRateOverridePct ?? ''}</td>
                <td>{p.max_tenure_months ?? p.maxTenureMonths ?? ''}</td>
                <td>{p.amb_factor ?? p.ambFactor ?? ''}</td>
                <td>{p.abb_emi_factor ?? p.abbEmiFactor ?? ''}</td>
                <td>
                  <button className="btn secondary small" onClick={(e) => { e.stopPropagation(); startEditProduct(p); }}>
                    Edit
                  </button>
                  <button className="btn danger small" onClick={(e) => { e.stopPropagation(); deleteProduct(p.id); }}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: '1em' }} ref={productEditorRef}>
          <h4>{productForm.id ? 'Edit Loan Product' : 'Add Loan Product'}</h4>
          <div style={{ marginBottom: '0.75em', opacity: 0.8 }}>
            {productForm.id ? 'Editing selected product. Update the fields below and save.' : 'Tip: click any row above to load a product here for editing.'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8em' }}>
            <label>
              Bank
              <select
                value={productForm.bankId}
                onChange={(e) => setProductField('bankId', e.target.value)}
              >
                <option value="">Select Bank</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Product
              <select
                value={productForm.product}
                onChange={(e) => setProductField('product', e.target.value)}
              >
                <option value="FOIR">Vanilla (FOIR)</option>
                <option value="DOUBLE_WHAMMY">Double Whammy</option>
                <option value="ABB">ABB</option>
                <option value="TURNOVER">Gross Turnover</option>
                <option value="LOW_LTV">Low LTV</option>
                <option value="LTV">Legacy LTV</option>
              </select>
            </label>

            <label>
              Max FOIR
              <input value={productForm.maxFOIR} onChange={(e) => setProductField('maxFOIR', e.target.value)} />
            </label>

            <label>
              Max LTV
              <input value={productForm.maxLTV} onChange={(e) => setProductField('maxLTV', e.target.value)} />
            </label>

            <label>
              Double Whammy Max FOIR + LTV Cap
              <input value={productForm.maxCombined} onChange={(e) => setProductField('maxCombined', e.target.value)} />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.6em', alignSelf: 'end' }}>
              <input
                type="checkbox"
                checked={!!productForm.enforceMaxLoanCap}
                onChange={(e) => setProductField('enforceMaxLoanCap', e.target.checked)}
              />
              Enforce Max Loan Cap
            </label>

            <label>
              Max Loan Cap
              <input
                value={productForm.maxLoanCap}
                onChange={(e) => setProductField('maxLoanCap', e.target.value)}
                disabled={!productForm.enforceMaxLoanCap}
                placeholder={productForm.enforceMaxLoanCap ? 'Enter cap amount' : 'Disabled unless enforced'}
              />
            </label>

            <label>
              Interest Rate (%)
              <input
                value={productForm.interestRateOverridePct}
                onChange={(e) => setProductField('interestRateOverridePct', e.target.value)}
              />
            </label>

            <label>
              Max Tenure (months)
              <input
                value={productForm.maxTenureMonths}
                onChange={(e) => setProductField('maxTenureMonths', e.target.value)}
              />
            </label>

            <label>
              AMB Factor (legacy)
              <input value={productForm.ambFactor} onChange={(e) => setProductField('ambFactor', e.target.value)} />
            </label>

            <label>
              ABB EMI Factor
              <input value={productForm.abbEmiFactor} onChange={(e) => setProductField('abbEmiFactor', e.target.value)} />
            </label>

            <label>
              Industry
              <select value={productForm.industry} onChange={(e) => setProductField('industry', e.target.value)}>
                <option value="">Industry</option>
                <option value="Trading">Trading</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Service">Service</option>
                <option value="Other">Other</option>
              </select>
            </label>

            <label>
              Turnover Factor (legacy)
              <input value={productForm.turnoverFactor} onChange={(e) => setProductField('turnoverFactor', e.target.value)} />
            </label>

            <label>
              Supported Property Types (comma separated)
              <input
                value={productForm.propertyTypesText}
                onChange={(e) => setProductField('propertyTypesText', e.target.value)}
                placeholder="Residential, Commercial, Plot, Industrial"
              />
            </label>
          </div>

          <div className="card" style={{ marginTop: '1em' }}>
            <h4 style={{ marginTop: 0 }}>Gross Turnover Sector Factors</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8em' }}>
              <label>
                Service
                <input
                  value={productForm.turnoverSectorFactors.Service}
                  onChange={(e) => setTurnoverFactorField('Service', e.target.value)}
                />
              </label>

              <label>
                Manufacturing
                <input
                  value={productForm.turnoverSectorFactors.Manufacturing}
                  onChange={(e) => setTurnoverFactorField('Manufacturing', e.target.value)}
                />
              </label>

              <label>
                Trading
                <input
                  value={productForm.turnoverSectorFactors.Trading}
                  onChange={(e) => setTurnoverFactorField('Trading', e.target.value)}
                />
              </label>

              <label>
                Distributor
                <input
                  value={productForm.turnoverSectorFactors.Distributor}
                  onChange={(e) => setTurnoverFactorField('Distributor', e.target.value)}
                />
              </label>

              <label>
                Other
                <input
                  value={productForm.turnoverSectorFactors.Other}
                  onChange={(e) => setTurnoverFactorField('Other', e.target.value)}
                />
              </label>
            </div>
          </div>

          <SlabEditor
            title="Salaried FOIR Slabs"
            slabs={productForm.foirSlabsSalaried}
            onChange={(rows) => setProductField('foirSlabsSalaried', rows)}
          />

          <SlabEditor
            title="Self Employed FOIR Slabs"
            slabs={productForm.foirSlabsSelfEmployed}
            onChange={(rows) => setProductField('foirSlabsSelfEmployed', rows)}
          />

          <SlabEditor
            title="ABB Steps (Average Bank Balance Ranges)"
            slabs={productForm.abbSteps}
            onChange={(rows) => setProductField('abbSteps', rows)}
          />

          <SlabEditor
            title="Low LTV Steps (Loan Amount Ranges)"
            slabs={productForm.lowLtvSteps}
            onChange={(rows) => setProductField('lowLtvSteps', rows)}
          />


          <div style={{ marginTop: '1em' }}>
            <button className="btn" onClick={saveProduct}>
              {productForm.id ? 'Update Product' : 'Add Product'}
            </button>

            {productForm.id ? (
              <button className="btn secondary" style={{ marginLeft: '1em' }} onClick={resetProductForm}>
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
